import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { setExceptionHandler, getLoaderBtn, getModalSwitch, handleReloadShellConfig } from '@app/store/actions';
import { ModalActionsComponent, PasslySelect, PasslyTextField } from '@app/components';
import { FormControl } from '@material-ui/core';
import { onPressEnterModal, genericErrorToast, defaultOrgId } from '@app/helpers';
import { vaultsService, vaultPasswordPolicyService, authService } from '@app/services';
import { IVaultPasswordPolicyDTO } from '@app/models';
import InitialGroupForMigratorComponent from '@app/pages/password/components/initialGroupForMigrator.component';
import { PasslyAutoComplete } from '@app/componentsV2';

export interface IReceivedData {
    handleRefresh: Function;
}

export const AddVaultDialog = (props: IReceivedData) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const currentOrgId = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const allOrgs = useSelector((state: any) => state.basic.shellConfig.organizations);

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [vaultName, setVaultName] = useState('');
    const [vaultNameInvalid, setVaultNameInvalid] = useState(false);
    const [formInvalid, setFormInvalid] = useState(true);
    const [description, setDescription] = useState('');
    const [onBehalfOf, setOnBehalfOf] = useState('');
    const [passwordPolicy, setPasswordPolicy] = useState<any>(null);
    const [passwordPolicies, setPasswordPolicies] = useState<any>(null);
    const [initialGroup, setInitialGroup] = useState('');

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        setOnBehalfOf(currentOrgId);

        vaultPasswordPolicyService.getAllVaultPasswordPolicies().then(handleVaultPasswordPolicySuccess).catch(genericErrorToast);
    }, []);

    const handleVaultPasswordPolicySuccess = (values: IVaultPasswordPolicyDTO[]) => {
        // set the default in the dropdown to the default policy
        const defaultPolicy: IVaultPasswordPolicyDTO = values.filter((item) => item.IsDefault)[0];
        defaultPolicy && setPasswordPolicy(defaultPolicy?.Id);

        const resultValues = values.map((value) => {
            return { name: value.DisplayName, code: value.Id };
        });
        setPasswordPolicies(resultValues);
    };

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'vaultName':
                setVaultName(value);
                break;
            case 'description':
                setDescription(value);
                break;
            case 'onBehalfOf':
                setOnBehalfOf(value);
                break;
            case 'passwordPolicy':
                setPasswordPolicy(value.code);
                break;
            default:
                break;
        }
    };
    const onVaultNameChange = (e: any) => {
        setErrorMessage('');
        onFormChange(e);

        const vaultChosen = e.target.value.trim();

        if (vaultChosen === '') {
            setVaultNameInvalid(true);
            setFormInvalid(true);
        } else {
            setVaultNameInvalid(false);
            setFormInvalid(false);
        }
    };

    const getInitialGroup = (group: string) => {
        setInitialGroup(group);
    };

    const handleCancel = () => {
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
    };

    const handleSaveBtn = () => {
        dispatch(getLoaderBtn(true));

        let jsonObject: any = {};

        jsonObject.displayName = vaultName;
        jsonObject.tenantId = null;
        jsonObject.onBehalfOfOrgId = onBehalfOf;

        vaultsService.isVaultNameAvailable(jsonObject, handleError, handleSuccessVaultNameValid);
    };

    const handleError = (response: any) => {
        dispatch(getLoaderBtn(false));

        genericErrorToast(response);
    };

    const handleSuccessVaultNameValid = (response: any) => {
        const result: boolean = response.data.value;

        if (result) {
            // save vault
            let jsonObject: any = {};

            jsonObject.OrganizationId = currentOrgId;
            jsonObject.DisplayName = vaultName;
            jsonObject.Description = description;
            jsonObject.OnBehalfOfOrgId = onBehalfOf;
            jsonObject.PasswordServerPolicyId = passwordPolicy;
            jsonObject.VaultGroups = [];

            if (initialGroup !== '') {
                jsonObject.VaultGroups.push({ Id: initialGroup });
            }

            vaultsService.addVault(jsonObject, handleError, handleSuccess);
        } else {
            dispatch(getLoaderBtn(false));
            setErrorMessage(translation('INVALID_TAKEN_VAULT_NAME'));
        }
    };

    const handleSuccess = () => {
        if (initialGroup !== '') {
            // refresh tokens so the user can see the passwords tab in the vault
            const callback = () => {
                dispatch(getLoaderBtn(false));
                dispatch(getModalSwitch(false));
                dispatch(handleReloadShellConfig());
                props.handleRefresh(true);
            };

            authService.getNewAccessToken(callback);
        } else {
            dispatch(getLoaderBtn(false));
            dispatch(getModalSwitch(false));
            props.handleRefresh(true);
        }
    };

    return (
        <>
                <form method='post' className='modal__form' tabIndex={0} onKeyPress={onPressEnterModal(handleSaveBtn, !vaultNameInvalid)}>
                    <div className='modal__body--row--space'>
                        <FormControl>
                            <PasslyTextField
                                name='vaultName'
                                label={'VAULT_NAME'}
                                value={vaultName}
                                onChange={onVaultNameChange}
                                id='vaultName-input'
                                inputProps={{ maxLength: 100 }}
                                autoFocus
                                required
                                error={vaultNameInvalid}
                                helperText={vaultNameInvalid && translation('REQUIRED_FIELD')}
                            />
                        </FormControl>
                    </div>
                    <div className='modal__body--row--space'>
                        <FormControl>
                            <PasslyTextField
                                name='description'
                                label={'DESCRIPTION'}
                                value={description}
                                onChange={onFormChange}
                                id='description-input'
                                inputProps={{ maxLength: 500 }}
                            />
                        </FormControl>
                    </div>
                    {currentOrgId === defaultOrgId && (
                        <div className='modal__body--row--space'>
                            <PasslySelect
                                name='onBehalfOf'
                                value={onBehalfOf}
                                onChange={onFormChange}
                                id='onBehalfOf-input'
                                label='ORGANIZATION'
                                optionLabel='name'
                                optionValue='id'
                                options={allOrgs}
                                appendTo={document.body}
                            />
                        </div>
                    )}
                    <div className='modal__body--row--space'>
                        <PasslyAutoComplete
                            className='passwordPolicy'
                            items={passwordPolicies}
                            onChange={onFormChange}
                            id='passwordPolicy-input'
                            name='passwordPolicy'
                            label={translation('VAULT_PASSWORD_POLICY')}
                            value={passwordPolicy}
                            dropdown={true}
                            appendTo={document.body}
                        />
                    </div>
                    <div className='modal__body--row--space'>
                        <InitialGroupForMigratorComponent returnGroup={getInitialGroup} />
                    </div>
                </form>

            {errorMessage && <div className='modal__error'>{errorMessage}</div>}

            <ModalActionsComponent
                primaryBtn={translation('ADD_VAULT')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSaveBtn}
                onClickSecondary={handleCancel}
                disabled={formInvalid}
                id='addVault'
            />
        </>
    );
};
